import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toLocale, type PublicConfig } from '@ousse/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { Alert, ErrorAlert, Field } from '../components/ui'

/** Après connexion : retour à la page demandée (`?next=/join/CODE`), sinon l'accueil. Liens internes seulement. */
const safeNext = (next: string | null) => (next && next.startsWith('/') && !next.startsWith('//') ? next : '/')

function useSubmit<T>(action: () => Promise<T>) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const submit = async (e?: FormEvent) => {
    e?.preventDefault()
    setPending(true)
    setError(null)
    try {
      return await action()
    } catch (err) {
      setError(err)
      return undefined
    } finally {
      setPending(false)
    }
  }
  return { pending, error, submit }
}

export function LoginPage() {
  const { t } = useTranslation()
  const { refresh } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const next = safeNext(params.get('next'))
  const { pending, error, submit } = useSubmit(async () => {
    await api.post('/auth/login', { email, password, client: 'web' })
    await refresh()
    navigate(next)
  })
  return (
    <>
      <h1>{t('auth.loginTitle')}</h1>
      <ErrorAlert error={error} />
      <form className="stack" onSubmit={submit}>
        <Field label={t('auth.email')} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button className="btn block" disabled={pending}>
          {t('auth.login')}
        </button>
      </form>
      <div className="stack" style={{ marginTop: '1.5rem' }}>
        <Link className="btn secondary block" to={`/login-link?next=${encodeURIComponent(next)}`}>
          {t('auth.loginWithEmail')}
        </Link>
        <Link to="/forgot-password">{t('auth.forgotPassword')}</Link>
        <p>
          {t('auth.noAccount')} <Link to={`/register${params.size ? `?${params}` : ''}`}>{t('auth.register')}</Link>
        </p>
      </div>
    </>
  )
}

export function RegisterPage() {
  const { t, i18n } = useTranslation()
  const { refresh } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const invitationCode = params.get('invitation') ?? undefined
  const config = useQuery({ queryKey: ['config'], queryFn: () => api.get<PublicConfig>('/config') })
  const [form, setForm] = useState({ email: '', password: '', displayName: '', accessCode: params.get('code') ?? '' })
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value })
  const { pending, error, submit } = useSubmit(async () => {
    await api.post('/auth/register', {
      email: form.email,
      password: form.password,
      displayName: form.displayName,
      locale: toLocale(i18n.language),
      accessCode: !invitationCode && form.accessCode ? form.accessCode : undefined,
      invitationCode,
      client: 'web',
    })
    await refresh()
    navigate('/')
  })
  const mode = config.data?.registrationMode
  if (mode === 'closed' && !invitationCode) {
    return (
      <>
        <h1>{t('auth.registerTitle')}</h1>
        <Alert kind="warning">{t('auth.registrationClosed')}</Alert>
      </>
    )
  }
  return (
    <>
      <h1>{t('auth.registerTitle')}</h1>
      <ErrorAlert error={error} />
      <form className="stack" onSubmit={submit}>
        <Field label={t('auth.displayName')} autoComplete="given-name" required value={form.displayName} onChange={set('displayName')} />
        <Field label={t('auth.email')} type="email" autoComplete="email" required value={form.email} onChange={set('email')} />
        <Field
          label={t('auth.password')}
          hint={t('auth.passwordHint')}
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          value={form.password}
          onChange={set('password')}
        />
        {mode === 'code' && !invitationCode && (
          <Field label={t('auth.accessCode')} hint={t('auth.accessCodeHint')} required autoCapitalize="characters" value={form.accessCode} onChange={set('accessCode')} />
        )}
        <button className="btn block" disabled={pending}>
          {t('auth.register')}
        </button>
      </form>
      <p style={{ marginTop: '1.5rem' }}>
        {t('auth.haveAccount')} <Link to={`/login${invitationCode ? `?next=/join/${invitationCode}` : ''}`}>{t('auth.login')}</Link>
      </p>
    </>
  )
}

export function LoginLinkPage() {
  const { t } = useTranslation()
  const { refresh } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token')
  const next = safeNext(params.get('next'))
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const request = useSubmit(async () => {
    await api.post('/auth/login-link/request', { email })
    setSent(true)
  })
  const consume = useSubmit(async () => {
    await api.post('/auth/login-link/consume', token ? { token, client: 'web' } : { email, code, client: 'web' })
    await refresh()
    navigate(next)
  })
  const started = useRef(false)
  useEffect(() => {
    // Le lien reçu par e-mail ne sert qu'une fois : on ne l'envoie qu'une seule fois (StrictMode compris).
    if (token && !started.current) {
      started.current = true
      void consume.submit()
    }
  }, [token, consume])

  if (token) {
    return (
      <>
        <h1>{t('auth.loginLinkTitle')}</h1>
        {consume.error ? <ErrorAlert error={consume.error} /> : <p role="status">{t('auth.consumingLink')}</p>}
        {Boolean(consume.error) && <Link to="/login-link">{t('auth.sendLink')}</Link>}
      </>
    )
  }
  return (
    <>
      <h1>{t('auth.loginLinkTitle')}</h1>
      <ErrorAlert error={request.error ?? consume.error} />
      {!sent ? (
        <form className="stack" onSubmit={request.submit}>
          <p>{t('auth.loginLinkIntro')}</p>
          <Field label={t('auth.email')} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <button className="btn block" disabled={request.pending}>
            {t('auth.sendLink')}
          </button>
        </form>
      ) : (
        <form className="stack" onSubmit={consume.submit}>
          <Alert kind="success">{t('auth.linkSent')}</Alert>
          <Field
            label={t('auth.code')}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          <button className="btn block" disabled={consume.pending}>
            {t('auth.useCode')}
          </button>
        </form>
      )}
    </>
  )
}

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const { pending, error, submit } = useSubmit(async () => {
    await api.post('/auth/password/forgot', { email })
    setSent(true)
  })
  return (
    <>
      <h1>{t('auth.forgotTitle')}</h1>
      <ErrorAlert error={error} />
      {sent ? (
        <Alert kind="success">{t('auth.resetSent')}</Alert>
      ) : (
        <form className="stack" onSubmit={submit}>
          <p>{t('auth.forgotIntro')}</p>
          <Field label={t('auth.email')} type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <button className="btn block" disabled={pending}>
            {t('auth.sendReset')}
          </button>
        </form>
      )}
    </>
  )
}

export function ResetPasswordPage() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const [password, setPassword] = useState('')
  const [done, setDone] = useState(false)
  const { pending, error, submit } = useSubmit(async () => {
    await api.post('/auth/password/reset', { token: params.get('token') ?? '', password })
    setDone(true)
  })
  return (
    <>
      <h1>{t('auth.resetTitle')}</h1>
      <ErrorAlert error={error} />
      {done ? (
        <>
          <Alert kind="success">{t('auth.resetDone')}</Alert>
          <Link className="btn" to="/login">
            {t('auth.login')}
          </Link>
        </>
      ) : (
        <form className="stack" onSubmit={submit}>
          <Field
            label={t('auth.newPassword')}
            hint={t('auth.passwordHint')}
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button className="btn block" disabled={pending}>
            {t('common.save')}
          </button>
        </form>
      )}
    </>
  )
}

export function VerifyEmailPage() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const { refresh } = useAuth()
  const [state, setState] = useState<'pending' | 'done' | unknown>('pending')
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    api
      .post('/auth/email/verify', { token: params.get('token') ?? '' })
      .then(async () => {
        setState('done')
        await refresh()
      })
      .catch((e: unknown) => setState(e))
  }, [params, refresh])
  return (
    <>
      <h1>{t('auth.verifyTitle')}</h1>
      {state === 'pending' && <p role="status">{t('common.loading')}</p>}
      {state === 'done' && <Alert kind="success">{t('auth.verifyDone')}</Alert>}
      {state !== 'pending' && state !== 'done' && <ErrorAlert error={state} />}
      <Link className="btn secondary" to="/">
        {t('nav.home')}
      </Link>
    </>
  )
}
